import { EV_PATHS } from "src/consts/Routes/Ev";
import { useTranslation } from "react-i18next";
import Text from "src/components/Displays/Text";
import { useCallback, useEffect, useState } from "react";
import { CarIcon, MoneyIcon, CalenderIcon } from "src/assets";
import { useNavigate, useSearchParams } from "react-router-dom";
import PrimaryButton from "src/components/Shared/PrimaryButton";
import { useSessionStorage } from "src/hooks/useSessionStorage";
import { SESSION_KEYS, UI_FLOW_PREFERENCES } from "src/consts/session";
import { Stack, Alert, Snackbar, SnackbarCloseReason } from "@mui/material";
import { smartChargingEvLightTransfer } from "src/api/smartChargingClients";

const Start = () => {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation("translation");
  const tid = searchParams.get("tid") || "";
  const spid = searchParams.get("spid") || "";

  const navigate = useNavigate();

  const [msg, setMsg] = useState("");
  const [open, setOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(true);

  const [, setUiFlowPreferences] = useSessionStorage<UI_FLOW_PREFERENCES>(
    SESSION_KEYS.UI_FLOW_PREFERENCES,
  );

  const [, setSalesPartnerId] = useSessionStorage<string>(
    SESSION_KEYS.SALES_PARTNER_ID,
  );
  const [getTransferId, setTransferId] = useSessionStorage<string>(
    SESSION_KEYS.TRANSFER_ID,
  );

  const transferIdFromSession = getTransferId();

  const getSmartChargingEvLightTransfer = useCallback(async () => {
    setIsLoading(true);
    try {
      if (transferIdFromSession === tid) {
        setIsLoading(false);
        return;
      }

      const response = await smartChargingEvLightTransfer({
        spid,
        tid,
      });
      setTransferId(tid);
      setSalesPartnerId(spid);
      setUiFlowPreferences({
        language: response.uiFlowPreferences.language,
        callbackUrlSuccess: response.uiFlowPreferences.callbackUrlSuccess,
        callbackUrlFailure: response.uiFlowPreferences.callbackUrlFailure,
        processType: response.uiFlowPreferences.processType,
        skipSaleProcess: response.uiFlowPreferences.skipSaleProcess.toString(),
      });

      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      const error = e as Error;
      setMsg(error.message ?? "");
      setOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getSmartChargingEvLightTransfer();
  }, [getSmartChargingEvLightTransfer]);

  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setOpen(false);
  };

  const navigateToCompList = () => {
    navigate(EV_PATHS.COMPATIBILITY);
  };

  const handleNextClick = () => {
    navigate(EV_PATHS.TERMS);
  };

  return (
    <Stack gap={5}>
      <Text
        text={t("page.evStart.pageTitle")}
        sx={(theme) => ({
          fontWeight: 500,
          fontSize: "32px",
          lineHeight: "40px",
          fontFamily: "officina",
          color: theme.palette.mainFont.primary,
        })}
      />

      <Stack gap={4}>
        <Stack direction="row" gap={4} alignItems="center">
          <Stack alignItems={"start"}>
            <MoneyIcon />
          </Stack>
          <Stack>
            <Text
              text={t("page.evStart.listItem1Title")}
              sx={(theme) => ({
                fontSize: "14px",
                fontWeight: "600",
                lineHeight: "24px",
                fontFamily: "Inter",
                color: "#2E2E2E",
              })}
            />

            <Text
              text={t("page.evStart.listItem1Description")}
              sx={(theme) => ({
                fontSize: "12px",
                fontWeight: "400",
                lineHeight: "20px",
                fontFamily: "Inter",
                color: "#2E2E2E",
              })}
            />
          </Stack>
        </Stack>

        <Stack direction="row" gap={4} alignItems="center">
          <Stack alignItems={"start"}>
            <CalenderIcon />
          </Stack>
          <Stack>
            <Text
              text={t("page.evStart.listItem2Title")}
              sx={(theme) => ({
                fontSize: "14px",
                fontWeight: "600",
                lineHeight: "24px",
                fontFamily: "Inter",
                color: "#2E2E2E",
              })}
            />

            <Text
              text={t("page.evStart.listItem2Description")}
              sx={(theme) => ({
                fontSize: "12px",
                fontWeight: "400",
                lineHeight: "20px",
                fontFamily: "Inter",
                color: "#2E2E2E",
              })}
            />
          </Stack>
        </Stack>

        <Stack direction="row" gap={4} alignItems="center">
          <Stack alignItems={"start"}>
            <CarIcon />
          </Stack>
          <Stack>
            <Text
              text={t("page.evStart.listItem3Title")}
              sx={(theme) => ({
                fontSize: "14px",
                fontWeight: "600",
                lineHeight: "24px",
                fontFamily: "Inter",
                color: "#2E2E2E",
              })}
            />

            <Text
              text={t("page.evStart.listItem3Description")}
              sx={(theme) => ({
                fontSize: "12px",
                fontWeight: "400",
                lineHeight: "20px",
                fontFamily: "Inter",
                color: "#2E2E2E",
              })}
            />
          </Stack>
        </Stack>
      </Stack>

      <PrimaryButton
        isLoading={isLoading}
        onClick={handleNextClick}
        disabled={msg !== ""}
        text={t("page.evStart.nextBtnText")}
      />

      <Stack>
        <PrimaryButton
          onClick={navigateToCompList}
          text={"Welche Autos sind kompatibel?"}
          sx={{
            border: "1px solid #00000038",
            backgroundColor: "transparent",
            color: "#3B3B3B",
          }}
        />
      </Stack>

      <Snackbar open={open} autoHideDuration={3000} onClose={handleClose}>
        <Alert
          severity="error"
          variant="filled"
          onClose={handleClose}
          sx={{ width: "100%" }}
        >
          {msg}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export default Start;
